import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (cents) => `$${(cents / 100).toFixed(2).replace(/\.00$/, "")}`;

const POLICIES = {
  "refund-policy": {
    title: "Refund Policy",
    updated: "September 2026",
    sections: [
      {
        heading: "Our promise",
        body: [
          "We stand behind N°001. If something isn't right with your order, email us at info@breethe.com.au with your order details and we'll sort it out — replacement, refund, or credit, whichever gets you sorted fastest.",
        ],
      },
      {
        heading: "Your rights under Australian Consumer Law",
        body: [
          "Nothing in this policy limits your rights under the Australian Consumer Law. You are entitled to a replacement or refund for a major failure, and compensation for any other reasonably foreseeable loss or damage. You are also entitled to have goods repaired or replaced if they fail to be of acceptable quality and the failure does not amount to a major failure.",
          "If your order arrives damaged, faulty, or isn't what you ordered, contact us within 30 days of delivery with a photo and we'll replace it or refund you in full — including any postage you paid.",
        ],
      },
      {
        heading: "Change of mind",
        body: [
          "Because our strips are a personal hygiene product, we can't accept returns of opened packs for change-of-mind. Unopened packs in original condition can be returned within 30 days of delivery for a refund of the product price — email us first and we'll send return instructions. Return postage for change-of-mind returns is on you.",
        ],
      },
      {
        heading: "Subscriptions",
        body: [
          "You can cancel your subscription anytime from the manage link in your receipt or at breethe.com.au/manage — no lock-ins, no cancellation fees. Cancellation stops future charges; packs already paid for and shipped aren't refundable unless faulty as above.",
        ],
      },
      {
        heading: "How refunds are paid",
        body: [
          "Approved refunds go back to your original payment method via Stripe, usually within 5-10 business days depending on your bank. We'll email you as soon as it's processed.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Service",
    updated: "September 2026",
    sections: [
      {
        heading: "The basics",
        body: [
          "These terms govern your use of breethe.com.au and purchases of Breethe products. By ordering from us you agree to these terms. Breethe is operated by Breethe Labs, Australia. You can reach us at info@breethe.com.au.",
        ],
      },
      {
        heading: "What our product is (and isn't)",
        body: [
          "Breethe N°001 is an external nasal strip designed to support easier nasal breathing during sport, training and sleep. It is not intended to diagnose, treat, cure or prevent any disease or medical condition, including sleep apnoea. If you have a medical condition, breathing difficulties, or skin sensitivities, talk to a healthcare professional before use. Stop use if irritation occurs.",
        ],
      },
      {
        heading: "Orders, pricing and payment",
        body: [
          "All prices are in Australian dollars and include GST. Orders are processed securely by Stripe — we never see or store your full card details. We may cancel and refund any order (for example due to pricing errors or stock issues); if we do, you'll be notified and refunded in full.",
          "Discount, referral and founder codes are single-use per their terms, can't be exchanged for cash, and may be disabled if abused.",
        ],
      },
      {
        heading: "Subscriptions",
        body: [
          "Subscriptions renew monthly at the price shown at signup until you cancel. Founding-member pricing is locked for as long as your subscription stays active; if you cancel and rejoin, current pricing applies.",
        ],
      },
      {
        heading: "Acceptable use",
        body: [
          "Don't misuse the site, attempt to break it, scrape it at volume, or use it for anything unlawful. We may suspend access for abuse.",
        ],
      },
      {
        heading: "Liability",
        body: [
          "To the extent permitted by law (and without limiting your Australian Consumer Law rights), our liability for any claim relating to a purchase is limited to the amount you paid for the relevant order. We aren't liable for indirect or consequential losses.",
        ],
      },
      {
        heading: "Governing law",
        body: [
          "These terms are governed by the laws of New South Wales, Australia.",
        ],
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "September 2026",
    sections: [
      {
        heading: "What we collect",
        body: [
          "When you buy, join the Founders List, leave a review or contact us, we collect the details you give us — name, email, shipping address, survey answers and review content. Payments are handled entirely by Stripe; we never see or store your full card number.",
          "Our site collects basic anonymous analytics (page views, country, device type). Visitors are identified only by a one-way hash — we don't store raw IP addresses and we don't use third-party advertising trackers.",
        ],
      },
      {
        heading: "How we use it",
        body: [
          "We use your details to fulfil and ship orders, send order and subscription emails, run the founders and referral programs, publish reviews you approve of, and respond to messages. We don't sell your data to anyone, full stop.",
        ],
      },
      {
        heading: "Service providers",
        body: [
          "We use Stripe (payments), Resend (transactional email), MongoDB (data storage) and our hosting platform to run the site. Each processes data only as needed to provide their service to us.",
        ],
      },
      {
        heading: "Your choices",
        body: [
          "Every marketing email has an unsubscribe path — just ask us. You can request a copy, correction or deletion of your personal information anytime by emailing info@breethe.com.au. We handle requests under the Australian Privacy Act 1988.",
        ],
      },
      {
        heading: "Data security and retention",
        body: [
          "We keep order records as required for tax and consumer-law purposes and delete or de-identify other personal data when it's no longer needed. Access to customer data is restricted to the Breethe team.",
        ],
      },
      {
        heading: "Contact",
        body: [
          "Privacy questions or complaints: info@breethe.com.au. If you're not satisfied with our response, you can contact the Office of the Australian Information Commissioner (oaic.gov.au).",
        ],
      },
    ],
  },
  "shipping-policy": {
    title: "Shipping Policy",
    updated: "September 2026",
    sections: [
      {
        heading: "Where and how we ship",
        body: [
          "We ship Australia-wide via Australia Post. Every order gets a tracking number, emailed to you as soon as it's posted. We currently ship within Australia only.",
        ],
      },
      {
        heading: "Postage costs",
        body: [
          "Postage is a flat $7.99 per order, and free on orders of $50 or more (after any discount codes). Subscription deliveries include free postage every month.",
        ],
      },
      {
        heading: "Delivery times",
        body: [
          "Orders are packed and posted within 1-2 business days. Australia Post standard delivery is typically 3-8 business days depending on where you are. Regional and remote areas can take a little longer.",
        ],
      },
      {
        heading: "Tracking and issues",
        body: [
          "You'll get a shipping confirmation email with your Australia Post tracking link. If your parcel hasn't arrived within 10 business days of posting, or arrives damaged, email info@breethe.com.au with your order details and we'll chase it up or make it right.",
        ],
      },
      {
        heading: "Address accuracy",
        body: [
          "Please double-check your shipping address at checkout — we ship to the address entered on the order. If a parcel comes back to us because of an incorrect address, we'll happily re-ship it once postage is re-covered.",
        ],
      },
    ],
  },
};

export default function Legal({ slugOverride }) {
  const { slug: paramSlug } = useParams();
  const slug = slugOverride || paramSlug;
  const [shipCfg, setShipCfg] = useState(null);
  const policy = POLICIES[slug];
  useEffect(() => {
    if (slug !== "shipping-policy") return;
    axios.get(`${API}/shipping/settings`)
      .then(({ data }) => setShipCfg(data))
      .catch(() => {});
  }, [slug]);
  if (!policy) return <Navigate to="/" replace />;

  const sections =
    slug === "shipping-policy" && shipCfg
      ? policy.sections.map((s) =>
          s.heading === "Postage costs"
            ? {
                ...s,
                body: [
                  shipCfg.free_all
                    ? "Postage is currently free on every order, anywhere in Australia. Subscription deliveries always include free postage."
                    : `Postage is a flat ${money(shipCfg.fee_cents)} per order, and free on orders of ${money(shipCfg.free_threshold_cents)} or more (after any discount codes). Subscription deliveries include free postage every month.`,
                ],
              }
            : s
        )
      : policy.sections;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <header className="px-6 md:px-10 py-6 border-b border-white/10 flex items-center justify-between">
        <Link to="/">
          <img src="/breethe-logo.webp" alt="Breethe" className="h-8 w-auto" />
        </Link>
        <span className="font-plex text-[10px] uppercase tracking-[0.3em] text-white/40">
          Legal
        </span>
      </header>

      <main className="flex-1 px-6 md:px-10 py-16">
        <div data-testid="legal-page" className="max-w-3xl mx-auto">
          <p className="font-plex text-xs uppercase tracking-[0.3em] text-volt mb-4">
            Last updated — {policy.updated}
          </p>
          <h1 className="font-anton uppercase text-4xl md:text-6xl leading-[0.9]">
            {policy.title}
            <span className="text-volt">.</span>
          </h1>

          <div className="mt-12 space-y-10">
            {sections.map((s) => (
              <section key={s.heading}>
                <h2 className="font-anton uppercase text-xl md:text-2xl text-white">
                  {s.heading}
                </h2>
                {s.body.map((p, i) => (
                  <p key={i} className="mt-3 text-white/60 font-light leading-relaxed">
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <Link
            to="/"
            data-testid="legal-back-home"
            className="mt-14 inline-block font-plex text-[11px] uppercase tracking-[0.25em] text-volt hover:text-volt-dark transition-colors duration-300"
          >
            Back to Breethe
          </Link>
        </div>
      </main>

      <footer className="px-6 md:px-10 py-6 border-t border-white/10">
        <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/30">
          © 2026 Breethe Labs — info@breethe.com.au
        </p>
      </footer>
    </div>
  );
}
